/**
 * Impressão de vendas (cupom não fiscal, A4, DANFE NFC-e/NF-e) a partir de
 * uma venda carregada do backend (GET /vendas/:id) e dos dados da organização.
 * Usado pelo PDV (movimentações F7) e pela tela de Pedidos/Orçamentos.
 */
import QRCode from 'qrcode';
import api from '../services/api';
import { larguraEfetivaCSS } from './impressaoMargem';
import { tentarImprimirDiretoPdf, blobParaBase64 } from './impressaoDireta';
import { impressaoDiretaAtiva } from './gaveta';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');

const fmtBRL = v =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Tolerante a maiúsculas/espaços — cadastros antigos ou editados fora da
// tela podem gravar 'SERVIÇO' em vez do 'serviço' canônico do select.
const ehServico = t => String(t || '').trim().toLowerCase() === 'serviço';

// Código do item mostrado nas impressões — mesma prioridade do XML fiscal
// (codigo_interno primeiro; produto_id só como reserva se faltar).
export const codigoItem = (i, idx) => i.codigo_interno || i.produto_id || (idx + 1);

export const fetchLogoBase64 = async (logoUrl) => {
  if (!logoUrl) return '';
  const src = logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`;
  try {
    const resp = await fetch(src);
    if (!resp.ok) return '';
    const blob = await resp.blob();
    return await new Promise(res => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.onerror = () => res('');
      reader.readAsDataURL(blob);
    });
  } catch { return ''; }
};

export const dadosOrg = (org) => ({
  nome:     org.nome_fantasia || org.razao_social || 'EMPRESA',
  cnpj:     (org.cnpj || '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'),
  endereco: [org.logradouro, org.numero, org.bairro].filter(Boolean).join(', '),
  cidadeUF: [org.cidade, org.uf].filter(Boolean).join('/'),
  fone:     org.telefone ? `Tel: ${org.telefone}` : '',
});

// Fecha o popup de impressão ao pressionar ESC — sem isso a janela só
// fecha pelo X ou Ctrl+W, o que trava o fluxo do PDV pra quem usa teclado.
export const fecharComEsc = (win) => {
  try {
    win.document.addEventListener('keydown', e => { if (e.key === 'Escape') win.close(); });
  } catch (_) {}
};

// Logo/QR são <img src="data:..."> — o navegador às vezes manda pro print
// antes de decodificar essas imagens (principalmente a primeira vez, sem
// cache), e a folha sai sem elas. Espera todas carregarem (ou 1.5s de
// segurança, pra nunca travar o print numa imagem que falhou) antes de
// chamar window.print().
export const aguardarImagens = (win) => {
  const imgs = Array.from(win.document.images || []);
  if (!imgs.length) return Promise.resolve();
  return Promise.race([
    Promise.all(imgs.map(img => img.complete
      ? Promise.resolve()
      : new Promise(res => { img.onload = res; img.onerror = res; }))),
    new Promise(res => setTimeout(res, 1500)),
  ]);
};

// Sempre abre a caixa de impressão normal (nunca imprime direto via QZ
// Tray) — este helper é usado só em reimpressões fora da finalização de
// venda no PDV (F7 e Pedidos/Orçamentos), onde o usuário precisa poder
// escolher impressora, ver na tela ou salvar em PDF. Impressão direta é
// exclusiva do fluxo de finalizar venda (funções locais em PDV.jsx).
const abrirEImprimir = async (html, largura) => {
  const w = largura === '58mm' ? 320 : largura === '80mm' ? 400 : 900;
  const win = window.open('', '_blank', `width=${w},height=700`);
  if (!win) { alert('Permita pop-ups para imprimir.'); return; }
  win.document.write(html);
  win.document.close();
  fecharComEsc(win);
  await aguardarImagens(win);
  win.focus();
  win.print();
};

// ── Cupom não fiscal (térmica) ─────────────────────────────────
export const gerarCupomVenda = async (v, largura, org = {}) => {
  const { nome, cnpj, endereco, cidadeUF } = dadosOrg(org);
  const logoData = await fetchLogoBase64(org.logo_url || '');
  const dataHora = new Date(v.criado_em).toLocaleString('pt-BR');
  const subtotal = (v.itens || []).reduce((acc, i) => acc + parseFloat(i.subtotal || 0), 0);
  const desconto = parseFloat(v.desconto || 0);
  const total    = parseFloat(v.total || 0);
  const taxaEnt  = parseFloat(v.taxa_entrega || 0);
  const pgtos    = v.pagamentos || [];
  const totalPagoV = pgtos.reduce((acc, p) => acc + parseFloat(p.valor || 0), 0);
  const trocoV   = parseFloat(v.troco || 0);
  const fs       = largura === '58mm' ? '9px' : '10px';
  const fsGrand  = largura === '58mm' ? '11px' : '13px';
  const fsTitulo = largura === '58mm' ? '12px' : '14px';
  const larguraCSS = larguraEfetivaCSS(largura);

  // Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos)
  const itemHtmlCupom = (i, idx) => `
  <div class="it">
    <p><b>${codigoItem(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.unidade||''} × R$ ${fmtBRL(parseFloat(i.preco_unitario))}</span>
      <span>R$ ${fmtBRL(parseFloat(i.subtotal))}</span>
    </p>
    ${parseFloat(i.desconto || 0) > 0 ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(parseFloat(i.preco_tabela))} — desconto item</span><span>– R$ ${fmtBRL(parseFloat(i.desconto))}</span></p>` : ''}
  </div>`;
  const somaSubtotalCupom = arr => arr.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
  const agruparCupom = !!org.habilitar_servicos && (v.itens || []).some(i => ehServico(i.tipo_item));
  const itensHtmlCupom = !agruparCupom
    ? (v.itens || []).map(itemHtmlCupom).join('')
    : (() => {
        const produtos = (v.itens || []).filter(i => !ehServico(i.tipo_item));
        const servicos = (v.itens || []).filter(i => ehServico(i.tipo_item));
        return `
  <p class="b" style="margin-top:2px;">📦 PRODUTOS</p>
  ${produtos.length ? produtos.map(itemHtmlCupom).join('') : '<p style="color:#888;">Nenhum produto.</p>'}
  <p class="row b"><span>Subtotal Produtos</span><span>R$ ${fmtBRL(somaSubtotalCupom(produtos))}</span></p>
  <hr class="hr">
  <p class="b">🧾 SERVIÇOS</p>
  ${servicos.length ? servicos.map(itemHtmlCupom).join('') : '<p style="color:#888;">Nenhum serviço.</p>'}
  <p class="row b"><span>Subtotal Serviços</span><span>R$ ${fmtBRL(somaSubtotalCupom(servicos))}</span></p>`;
      })();

  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Cupom #${v.id}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:${fs}; font-weight:600; width:${larguraCSS}; padding:4px; }
  .c { text-align:center; } .b { font-weight:bold; }
  .hr { border:none; border-top:1px dashed #000; margin:4px 0; }
  .row { display:flex; justify-content:space-between; }
  .it { margin:2px 0; } .grand { font-size:${fsGrand}; }
  @media print { @page { margin:0; size:${larguraCSS} auto; } }
</style></head><body>
  ${logoData ? `<div class="c" style="margin-bottom:4px;"><img src="${logoData}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : ''}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${nome}</p>
  ${cnpj ? `<p class="c">CNPJ: ${cnpj}</p>` : ''}
  ${endereco ? `<p class="c">${endereco}</p>` : ''}
  ${cidadeUF ? `<p class="c">${cidadeUF}</p>` : ''}
  <hr class="hr">
  <p class="c b">CUPOM NÃO FISCAL</p>
  <p class="c">${dataHora}</p>
  <p class="row"><span>Venda nº:</span><span class="b">${v.id}</span></p>
  <p class="row"><span>Cliente:</span><span>${v.cliente_nome || 'Consumidor Final'}</span></p>
  <hr class="hr">
  <p class="b">ITENS</p>
  ${itensHtmlCupom}
  <hr class="hr">
  <p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>
  ${agruparCupom ? `
  <p class="row" style="font-size:8px;color:#333;">&nbsp;&nbsp;<span>📦 Total Produtos</span><span>R$ ${fmtBRL(somaSubtotalCupom((v.itens || []).filter(i => !ehServico(i.tipo_item))))}</span></p>
  <p class="row" style="font-size:8px;color:#333;">&nbsp;&nbsp;<span>🧾 Total Serviços</span><span>R$ ${fmtBRL(somaSubtotalCupom((v.itens || []).filter(i => ehServico(i.tipo_item))))}</span></p>` : ''}
  ${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}
  ${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  <hr class="hr">
  <p class="b">PAGAMENTOS</p>
  ${pgtos.map(p => `<p class="row"><span>${p.forma_pagamento}</span><span>R$ ${fmtBRL(parseFloat(p.valor))}</span></p>`).join('')}
  <hr class="hr">
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPagoV + trocoV)}</span></p>
  ${trocoV > 0 ? `<p class="row"><span>Troco</span><span>R$ ${fmtBRL(trocoV)}</span></p>
  <p class="row b" style="border-top:1px dashed #999;padding-top:4px;"><span>TOTAL LÍQUIDO</span><span>R$ ${fmtBRL(totalPagoV)}</span></p>` : ''}
  <hr class="hr">
  <p class="c" style="margin-top:6px;font-size:9px;">Obrigado pela preferência!</p>
  <p class="c" style="font-size:8px;">É MAIS GESTOR — cupom não fiscal</p>
</body></html>`;

  abrirEImprimir(html, largura);
};

// ── Pedido/venda em A4 ─────────────────────────────────────────
export const gerarA4Venda = async (v, org = {}) => {
  const { nome, cnpj, endereco, cidadeUF } = dadosOrg(org);
  const cep      = org.cep ? `CEP ${org.cep}` : '';
  const endCid   = [endereco, cidadeUF, cep].filter(Boolean).join(' — ');
  const fone     = org.telefone ? `Fone: ${org.telefone}` : '';
  const logoData = await fetchLogoBase64(org.logo_url || '');
  const logoHtml = logoData
    ? `<img src="${logoData}" style="max-height:55px;max-width:120px;object-fit:contain;" />`
    : `<div style="width:80px;height:60px;border:1px dashed #aaa;display:flex;align-items:center;justify-content:center;font-size:9px;color:#aaa;">LOGO</div>`;
  const dataHora  = new Date(v.criado_em).toLocaleString('pt-BR');
  // Vendedor de verdade (comissão) quando informado — ex.: "Solicitar vendedor"
  // no F4 de Pedidos/Orçamentos ou no PDV; sem isso, mostra quem operou a venda.
  const vendedorExibicao = v.vendedor_nome || v.usuario_nome || '—';
  const itensV    = v.itens || [];
  const pgtos     = v.pagamentos || [];
  const subtotal  = itensV.reduce((acc, i) => acc + parseFloat(i.subtotal || 0), 0);
  const desconto  = parseFloat(v.desconto || 0);
  const total     = parseFloat(v.total || 0);
  const taxaEnt   = parseFloat(v.taxa_entrega || 0);
  const totalPagoV = pgtos.reduce((acc, p) => acc + parseFloat(p.valor || 0), 0);
  const trocoV    = parseFloat(v.troco || 0);
  const qtdTotal  = itensV.reduce((acc, i) => acc + parseFloat(i.quantidade || 0), 0);

  // Agrupamento Produtos x Serviços (parâmetro organizacoes.habilitar_servicos)
  const itemRowA4Venda = (i, idx) => `
    <tr>
      <td class="right">${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})}</td>
      <td>${codigoItem(i, idx)}</td>
      <td>${i.descricao}</td>
      <td>${i.unidade||''}</td>
      <td class="right">${fmtBRL(parseFloat(i.preco_unitario))}</td>
      <td class="right">${(() => {
        const tab = parseFloat(i.preco_tabela || 0), un = parseFloat(i.preco_unitario || 0);
        return tab > un ? ((1 - un / tab) * 100).toFixed(2).replace('.', ',') : '0,00';
      })()}%</td>
      <td class="right">${fmtBRL(parseFloat(i.subtotal))}</td>
    </tr>`;
  const somaSubtotalA4Venda = arr => arr.reduce((s, i) => s + parseFloat(i.subtotal || 0), 0);
  const agruparA4Venda = !!org.habilitar_servicos && itensV.some(i => ehServico(i.tipo_item));
  const linhasItensA4Venda = !agruparA4Venda
    ? itensV.map(itemRowA4Venda).join('')
    : (() => {
        const produtos = itensV.filter(i => !ehServico(i.tipo_item));
        const servicos = itensV.filter(i => ehServico(i.tipo_item));
        const grupoRow = label => `<tr><td colspan="7" style="background:#eef2ff;color:#1e3a8a;font-weight:bold;padding:5px 6px;">${label}</td></tr>`;
        const vazioRow = label => `<tr><td colspan="7" style="text-align:center;color:#999;font-style:italic;">${label}</td></tr>`;
        const subtotalRow = (label, valor) => `<tr><td colspan="6" class="right" style="font-weight:bold;background:#f8fafc;">${label}</td><td class="right" style="font-weight:bold;background:#f8fafc;">${fmtBRL(valor)}</td></tr>`;
        return grupoRow('📦 PRODUTOS')
          + (produtos.length ? produtos.map(itemRowA4Venda).join('') : vazioRow('Nenhum produto.'))
          + subtotalRow('Subtotal Produtos R$:', somaSubtotalA4Venda(produtos))
          + grupoRow('🧾 SERVIÇOS')
          + (servicos.length ? servicos.map(itemRowA4Venda).join('') : vazioRow('Nenhum serviço.'))
          + subtotalRow('Subtotal Serviços R$:', somaSubtotalA4Venda(servicos));
      })();

  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Venda #${v.id}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:Arial,sans-serif; font-size:12pt; color:#000; padding:16px 20px; }
  .cabecalho { display:flex; align-items:flex-start; justify-content:space-between; border-bottom:2px solid #000; padding-bottom:8px; margin-bottom:6px; }
  .cab-centro { text-align:center; flex:1; }
  .cab-centro .razao { font-size:16pt; font-weight:bold; }
  .cab-direita { text-align:right; white-space:nowrap; }
  .cab-direita .nr { font-size:14pt; font-weight:bold; }
  .aviso { background:#f5f5f5; border:1px solid #ccc; padding:4px 8px; font-size:10pt; text-align:center; font-style:italic; margin-bottom:8px; }
  .info-linha { display:flex; gap:24px; margin-bottom:8px; font-size:12pt; }
  .info-linha span { font-weight:bold; }
  table { width:100%; border-collapse:collapse; margin-bottom:6px; }
  th { background:#222; color:#fff; padding:4px 6px; font-size:11pt; text-align:left; }
  td { padding:3px 6px; border-bottom:1px solid #ddd; font-size:11pt; }
  .right { text-align:right; }
  .totais-bloco { display:flex; justify-content:flex-end; margin-bottom:8px; }
  .totais-tabela { width:260px; }
  .totais-tabela td { border:none; padding:2px 6px; }
  .totais-tabela .total-geral { font-size:14pt; font-weight:bold; border-top:2px solid #000; }
  .pgto-titulo { font-weight:bold; margin:8px 0 4px; font-size:12pt; }
  .pgto-resumo { display:flex; gap:32px; margin-top:6px; font-size:12pt; }
  .obs-bloco { border:1px solid #ccc; border-radius:4px; padding:8px; margin-bottom:8px; font-size:12pt; }
  .rodape-assinaturas { display:flex; margin-top:24px; border-top:1px solid #000; }
  .rodape-campo { flex:1; padding:8px 4px 4px; border-right:1px solid #000; text-align:center; font-size:11pt; color:#555; }
  .rodape-campo:last-child { border-right:none; }
  .rodape-vedado { text-align:center; font-size:10pt; color:#888; margin-top:6px; }
  @media print {
    @page { size:A4 portrait; margin:12mm 14mm; }
    body { padding:0; }
  }
</style></head><body>

<div class="cabecalho">
  <div style="display:flex;align-items:center;">${logoHtml}</div>
  <div class="cab-centro">
    <div class="razao">${nome}</div>
    ${cnpj ? `<div>CNPJ: ${cnpj}</div>` : ''}
    ${endCid ? `<div>${endCid}</div>` : ''}
    ${fone ? `<div>${fone}</div>` : ''}
  </div>
  <div class="cab-direita">
    <div class="nr">VENDA Nº ${String(v.id).padStart(9,'0')}</div>
    <div>${dataHora}</div>
    <div>Vendedor: ${vendedorExibicao}</div>
  </div>
</div>

<div class="aviso">NÃO É DOCUMENTO FISCAL – NÃO É VÁLIDO COMO RECIBO E COMO GARANTIA DE MERCADORIA – NÃO COMPROVA PAGAMENTO</div>

<div class="info-linha">
  <div>Data/Hora: <span>${dataHora}</span></div>
  <div>Vendedor: <span>${vendedorExibicao}</span></div>
  <div>Cliente: <span>${v.cliente_nome || 'Consumidor Final'}</span></div>
</div>

<table>
  <thead>
    <tr>
      <th class="right" style="width:55px">Quant.</th>
      <th style="width:65px">Código</th>
      <th>Descrição</th>
      <th style="width:35px">UN</th>
      <th class="right" style="width:75px">Preço R$</th>
      <th class="right" style="width:55px">Desc.%</th>
      <th class="right" style="width:80px">TOTAL R$</th>
    </tr>
  </thead>
  <tbody>
    ${linhasItensA4Venda}
  </tbody>
</table>

<div class="totais-bloco">
  <table class="totais-tabela">
    <tr><td>Qtd. produtos:</td><td class="right">${Number(qtdTotal).toLocaleString('pt-BR',{minimumFractionDigits:3})}</td></tr>
    <tr><td>Produtos R$:</td><td class="right">${fmtBRL(subtotal)}</td></tr>
    ${agruparA4Venda ? `
    <tr><td style="font-size:9px;color:#555;">&nbsp;&nbsp;📦 Total Produtos:</td><td class="right" style="font-size:9px;color:#555;">${fmtBRL(somaSubtotalA4Venda(itensV.filter(i => !ehServico(i.tipo_item))))}</td></tr>
    <tr><td style="font-size:9px;color:#555;">&nbsp;&nbsp;🧾 Total Serviços:</td><td class="right" style="font-size:9px;color:#555;">${fmtBRL(somaSubtotalA4Venda(itensV.filter(i => ehServico(i.tipo_item))))}</td></tr>` : ''}
    ${(() => {
      const di = itensV.reduce((a, i) => a + Math.max(0, parseFloat(i.desconto || 0)), 0);
      return di > 0 ? `<tr><td>Desconto em itens R$:</td><td class="right">– ${fmtBRL(di)}</td></tr>` : '';
    })()}
    ${desconto > 0 ? `<tr><td>Desconto R$:</td><td class="right">– ${fmtBRL(desconto)}</td></tr>` : ''}
    ${taxaEnt > 0 ? `<tr><td>Taxa de entrega R$:</td><td class="right">+ ${fmtBRL(taxaEnt)}</td></tr>` : ''}
    <tr class="total-geral"><td>TOTAL R$:</td><td class="right">${fmtBRL(total)}</td></tr>
  </table>
</div>

<div class="pgto-titulo">Formas de Pagamento</div>
<table>
  <thead>
    <tr>
      <th style="width:40px">Nº</th>
      <th>Vencimento</th>
      <th class="right">Valor R$</th>
      <th>F. Pagamento</th>
    </tr>
  </thead>
  <tbody>
    ${pgtos.map((p,idx) => `
    <tr>
      <td>${idx+1}</td>
      <td>${dataHora.split(' ')[0]}</td>
      <td class="right">${fmtBRL(parseFloat(p.valor))}</td>
      <td>${p.forma_pagamento}</td>
    </tr>`).join('')}
  </tbody>
</table>

<div class="pgto-resumo">
  <div>Pagamento: <strong>À Vista</strong></div>
  <div>Total negociação R$: <strong>${fmtBRL(total)}</strong></div>
  <div>Total formas pagamento R$: <strong>${fmtBRL(totalPagoV)}</strong></div>
  ${trocoV > 0 ? `<div>Troco R$: <strong>${fmtBRL(trocoV)}</strong></div>` : ''}
</div>

${v.observacoes ? `<div class="obs-bloco"><b>Observações:</b><br>${v.observacoes}</div>` : ''}

<div class="rodape-assinaturas">
  <div class="rodape-campo">DATA DO ACEITE</div>
  <div class="rodape-campo">RAZÃO SOCIAL</div>
  <div class="rodape-campo">CLIENTE</div>
</div>
<div class="rodape-vedado">É vedada a autenticação deste documento</div>

</body></html>`;

  abrirEImprimir(html, 'a4');
};

// ── DANFE NFC-e (térmica) a partir da venda + resultado da emissão ──
// Documento FISCAL: a data/hora impressa é a da EMISSÃO na SEFAZ
// (resultado.data_emissao — momento do envio), não a da venda. A data da
// venda permanece nos cupons gerenciais (térmica/A4) e relatórios.
export const gerarDanfeNfceVenda = async (v, resultado, largura, org = {}) => {
  // Abre o popup imediatamente (contexto de gesto do usuário) para não ser bloqueado
  const w = largura === '58mm' ? 320 : 400;
  const win = window.open('', '_blank', `width=${w},height=700`);
  if (!win) { alert('Permita pop-ups para imprimir o DANFE.'); return; }
  win.document.write('<html><body style="font-family:monospace;padding:8px;">Gerando DANFE…</body></html>');

  const { nome, cnpj, endereco, cidadeUF, fone } = dadosOrg(org);
  const chave    = resultado.chave_acesso || '';
  const proto    = resultado.protocolo    || '';
  const qrUrl    = resultado.qr_code_url  || '';
  const urlChave = resultado.url_consulta || '';
  const emit     = resultado.emitente     || {};
  const emNome   = emit.nome_fantasia || emit.razao_social || nome;
  const emCnpj   = emit.cnpj ? emit.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : cnpj;
  const emEnd    = [emit.logradouro, emit.numero, emit.bairro].filter(Boolean).join(', ') || endereco;
  const emCidade = [emit.cidade, emit.uf].filter(Boolean).join('/') || cidadeUF;
  const isHomol  = resultado.ambiente === 'homologacao';

  const dataHora = new Date(resultado.data_emissao || Date.now()).toLocaleString('pt-BR');
  // NFC-e é documento de MERCADORIA — item de serviço nunca entra no DANFE
  // (serviço vai por NFS-e, ainda não implementada). Desconto E PAGAMENTOS
  // reajustados proporcionalmente à parte de mercadoria, igual ao que o
  // backend faz na emissão real — senão o DANFE mostraria o valor pago da
  // venda inteira contra um TOTAL só de produto, e o troco calculado bateria
  // errado (mostraria como troco o que na real é a parte do serviço).
  const todosItens = v.itens || [];
  const itensV   = todosItens.filter(i => !ehServico(i.tipo_item));
  const removeuServico = itensV.length < todosItens.length;
  const subtotalTodos = todosItens.reduce((acc, i) => acc + parseFloat(i.subtotal || 0), 0);
  const subtotal = itensV.reduce((acc, i) => acc + parseFloat(i.subtotal || 0), 0);
  const proporcao = subtotalTodos > 0 ? subtotal / subtotalTodos : 1;
  const descontoOriginal = parseFloat(v.desconto || 0);
  const desconto = removeuServico ? +(descontoOriginal * proporcao).toFixed(2) : descontoOriginal;
  const taxaEnt  = parseFloat(v.taxa_entrega || 0);
  const total    = removeuServico ? +(subtotal - desconto + taxaEnt).toFixed(2) : parseFloat(v.total || 0);
  const pgtosTodos = v.pagamentos || [];
  const pgtos    = removeuServico
    ? pgtosTodos.map(p => ({ ...p, valor: +(parseFloat(p.valor || 0) * proporcao).toFixed(2) }))
    : pgtosTodos;
  const totalPagoVal = pgtos.reduce((acc, p) => acc + parseFloat(p.valor || 0), 0);
  const trocoVal = Math.max(0, totalPagoVal - total);
  const fs       = largura === '58mm' ? '9px' : '10px';
  const fsGrand  = largura === '58mm' ? '11px' : '13px';
  const fsTitulo = largura === '58mm' ? '12px' : '14px';
  const larguraCSS = larguraEfetivaCSS(largura);

  const chaveFormatada = chave.replace(/(\d{4})/g, '$1 ').trim();

  // width maior que o tamanho impresso (200px de dados pra ~150px na folha) —
  // fica mais nítido na térmica, que não faz antialiasing/escala de cinza.
  let qrDataUrl = '';
  if (qrUrl) {
    try { qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 2, errorCorrectionLevel: 'M' }); } catch (_) {}
  }
  const logoData = await fetchLogoBase64(org.logo_url || '');
  const logoHtml = logoData ? `<div class="c" style="margin-bottom:4px;"><img src="${logoData}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : '';

  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>DANFE NFC-e #${v.id}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:${fs};font-weight:600;width:${larguraCSS};padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}.r{text-align:right;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .it{margin:2px 0;}.grand{font-size:${fsGrand};}
  .chave{word-break:break-all;font-size:8px;letter-spacing:0.5px;}
  .homol{background:#fff3cd;border:1px solid #ffc107;padding:2px 4px;text-align:center;font-size:8px;margin:3px 0;}
  @media print{@page{margin:0;size:${larguraCSS} auto;}}
</style></head><body>
  ${isHomol ? '<div class="homol">AMBIENTE DE HOMOLOGAÇÃO — SEM VALOR FISCAL</div>' : ''}
  ${logoHtml}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${emNome}</p>
  <p class="c">CNPJ: ${emCnpj}</p>
  ${emEnd    ? `<p class="c">${emEnd}</p>` : ''}
  ${emCidade ? `<p class="c">${emCidade}</p>` : ''}
  ${fone     ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">DANFE NFC-e</p>
  <p class="c" style="font-size:8px;">Documento Auxiliar da NFC-e</p>
  <p class="c">${dataHora}</p>
  <p class="row"><span>NFC-e nº:</span><span class="b">${resultado.numero ?? '—'}</span> <span>Série: ${resultado.serie || '1'}</span></p>
  ${v.cliente_nome ? `<p class="row"><span>Cliente:</span><span>${v.cliente_nome}</span></p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">ITENS</p>
  ${itensV.map((i, idx) => `
  <div class="it">
    <p><b>${codigoItem(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.unidade||''} × R$ ${fmtBRL(parseFloat(i.preco_unitario))}</span>
      <span class="b">R$ ${fmtBRL(parseFloat(i.subtotal))}</span>
    </p>
    ${parseFloat(i.desconto || 0) > 0 ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(parseFloat(i.preco_tabela))} — desconto item</span><span>– R$ ${fmtBRL(parseFloat(i.desconto))}</span></p>` : ''}
  </div>`).join('')}
  <hr class="hr">
  <p class="row"><span>Qtd. itens:</span><span>${itensV.length}</span></p>
  ${(desconto > 0 || taxaEnt > 0) ? `<p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>` : ''}${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  ${removeuServico ? `<p style="font-size:8px;color:#666;margin-top:2px;">* Total desta NFC-e é só de mercadoria. Itens de serviço da venda não entram aqui (NFS-e ainda não implementada).</p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">PAGAMENTOS</p>
  ${pgtos.map(p => `<p class="row"><span>${p.forma_pagamento}</span><span>R$ ${fmtBRL(parseFloat(p.valor))}</span></p>`).join('')}
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPagoVal)}</span></p>
  ${trocoVal > 0 ? `<p class="row b"><span>TROCO</span><span>R$ ${fmtBRL(trocoVal)}</span></p>` : ''}
  ${removeuServico ? `<p style="font-size:8px;color:#666;">(valores pagos rateados — esta venda inclui serviço, fora desta NFC-e)</p>` : ''}
  <hr class="hr">
  <p class="c b" style="font-size:9px;">AUTORIZADO O USO DA NFC-e</p>
  <p class="c" style="font-size:8px;margin-top:2px;">Protocolo: ${proto}</p>
  <p class="chave" style="margin:4px 0;">${chaveFormatada}</p>
  ${qrDataUrl ? `<div class="c" style="margin:6px 0;"><img src="${qrDataUrl}" width="150" height="150" style="image-rendering:pixelated;" alt="QR Code"/></div>` : ''}
  ${urlChave ? `<p class="c" style="font-size:8px;">Consulte em: ${urlChave}</p>` : ''}
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
  fecharComEsc(win);
  await aguardarImagens(win);
  win.focus();
  win.print();
};

// ── Prévia DANFE NFC-e a partir de uma venda JÁ SALVA (mas ainda não
// transmitida à SEFAZ) — usada quando a venda foi finalizada em outra tela
// (ex.: Pedidos/Orçamentos) antes de o operador escolher NFC-e/NF-e, pra dar
// uma chance de conferir os dados antes de gastar uma emissão real. Mesmo
// layout do DANFE oficial (gerarDanfeNfceVenda), mas sem chave/protocolo/QR
// (ainda não existem) e com a marca d'água "NÃO TRANSMITIDO".
export const gerarPreviewDanfeNfceVenda = async (v, largura, org = {}) => {
  const w = largura === '58mm' ? 320 : 400;
  const win = window.open('', '_blank', `width=${w},height=700`);
  if (!win) { alert('Permita pop-ups para imprimir.'); return; }
  win.document.write('<html><body style="font-family:monospace;padding:8px;">Gerando prévia…</body></html>');

  const MARCA_AGUA = 'NÃO TRANSMITIDO — SEM VALOR FISCAL';
  const { nome, cnpj, endereco, cidadeUF, fone } = dadosOrg(org);
  const dataHora = new Date().toLocaleString('pt-BR');

  const todosItens = v.itens || [];
  const itensV = todosItens.filter(i => !ehServico(i.tipo_item));
  const removeuServico = itensV.length < todosItens.length;
  const subtotalTodos = todosItens.reduce((acc, i) => acc + parseFloat(i.subtotal || 0), 0);
  const subtotal = itensV.reduce((acc, i) => acc + parseFloat(i.subtotal || 0), 0);
  const proporcao = subtotalTodos > 0 ? subtotal / subtotalTodos : 1;
  const descontoOriginal = parseFloat(v.desconto || 0);
  const desconto = removeuServico ? +(descontoOriginal * proporcao).toFixed(2) : descontoOriginal;
  const taxaEnt  = parseFloat(v.taxa_entrega || 0);
  const total    = removeuServico ? +(subtotal - desconto + taxaEnt).toFixed(2) : parseFloat(v.total || 0);
  const pgtosTodos = v.pagamentos || [];
  const pgtos = removeuServico
    ? pgtosTodos.map(p => ({ ...p, valor: +(parseFloat(p.valor || 0) * proporcao).toFixed(2) }))
    : pgtosTodos;
  const totalPagoVal = pgtos.reduce((acc, p) => acc + parseFloat(p.valor || 0), 0);
  const trocoVal = Math.max(0, totalPagoVal - total);
  const fs       = largura === '58mm' ? '9px' : '10px';
  const fsGrand  = largura === '58mm' ? '11px' : '13px';
  const fsTitulo = largura === '58mm' ? '12px' : '14px';
  const larguraCSS = larguraEfetivaCSS(largura);

  const logoData = await fetchLogoBase64(org.logo_url || '');
  const logoHtml = logoData ? `<div class="c" style="margin-bottom:4px;"><img src="${logoData}" style="max-width:180px;max-height:70px;object-fit:contain;" /></div>` : '';

  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>Prévia DANFE NFC-e #${v.id}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Courier New',Courier,monospace;font-size:${fs};font-weight:600;width:${larguraCSS};padding:4px;}
  .c{text-align:center;}.b{font-weight:bold;}
  .hr{border:none;border-top:1px dashed #000;margin:4px 0;}
  .row{display:flex;justify-content:space-between;}
  .it{margin:2px 0;}.grand{font-size:${fsGrand};}
  .chave{word-break:break-all;font-size:8px;letter-spacing:0.5px;color:#aaa;}
  .nao-transmitido{
    background:#fff3cd;border:2px solid #e65c00;
    padding:4px;text-align:center;font-weight:bold;
    font-size:9px;color:#b83000;margin:4px 0;letter-spacing:0.5px;
  }
  @media print{@page{margin:0;size:${larguraCSS} auto;}}
</style></head><body>
  <div class="nao-transmitido">${MARCA_AGUA}</div>
  ${logoHtml}
  <p class="c b" style="font-size:${fsTitulo};margin-bottom:2px;">${nome}</p>
  <p class="c">CNPJ: ${cnpj}</p>
  ${endereco ? `<p class="c">${endereco}</p>` : ''}
  ${cidadeUF ? `<p class="c">${cidadeUF}</p>` : ''}
  ${fone     ? `<p class="c">${fone}</p>` : ''}
  <hr class="hr">
  <p class="c b">DANFE NFC-e — PRÉVIA</p>
  <p class="c" style="font-size:8px;">Documento Auxiliar da NFC-e</p>
  <p class="c">${dataHora} — Venda nº ${v.id}</p>
  ${v.cliente_nome ? `<p class="row"><span>Cliente:</span><span>${v.cliente_nome}</span></p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">ITENS</p>
  ${!itensV.length ? `<p style="color:#b83000;font-size:10px;">Esta venda só tem serviço(s) — NFC-e não pode ser emitida sem nenhum item de mercadoria.</p>` : ''}
  ${itensV.map((i, idx) => `
  <div class="it">
    <p><b>${codigoItem(i, idx)}</b> ${i.descricao}</p>
    <p class="row">
      <span>${Number(i.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:3})} ${i.unidade||''} × R$ ${fmtBRL(parseFloat(i.preco_unitario))}</span>
      <span class="b">R$ ${fmtBRL(parseFloat(i.subtotal))}</span>
    </p>
    ${parseFloat(i.desconto || 0) > 0 ? `<p class="row" style="font-size:9px;color:#555;"><span>de R$ ${fmtBRL(parseFloat(i.preco_tabela))} — desconto item</span><span>– R$ ${fmtBRL(parseFloat(i.desconto))}</span></p>` : ''}
  </div>`).join('')}
  <hr class="hr">
  <p class="row"><span>Qtd. itens:</span><span>${itensV.length}</span></p>
  ${(desconto > 0 || taxaEnt > 0) ? `<p class="row"><span>Subtotal</span><span>R$ ${fmtBRL(subtotal)}</span></p>` : ''}${desconto > 0 ? `<p class="row"><span>Desconto</span><span>– R$ ${fmtBRL(desconto)}</span></p>` : ''}${taxaEnt > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaEnt)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(total)}</span></p>
  ${removeuServico ? `<p style="font-size:8px;color:#666;margin-top:2px;">* Total desta NFC-e é só de mercadoria. Itens de serviço da venda não entram aqui (NFS-e ainda não implementada).</p>` : ''}
  <hr class="hr">
  <p class="b" style="margin-bottom:2px;">PAGAMENTOS</p>
  ${pgtos.map(p => `<p class="row"><span>${p.forma_pagamento}</span><span>R$ ${fmtBRL(parseFloat(p.valor))}</span></p>`).join('')}
  <p class="row"><span>Total pago</span><span>R$ ${fmtBRL(totalPagoVal)}</span></p>
  ${trocoVal > 0 ? `<p class="row b"><span>TROCO</span><span>R$ ${fmtBRL(trocoVal)}</span></p>` : ''}
  ${removeuServico ? `<p style="font-size:8px;color:#666;">(valores pagos rateados — esta venda inclui serviço, fora desta NFC-e)</p>` : ''}
  <hr class="hr">
  <p class="chave" style="margin:4px 0;">Chave: (será gerada após transmissão)</p>
  <div class="nao-transmitido" style="margin-top:6px;">${MARCA_AGUA}</div>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
  fecharComEsc(win);
  await aguardarImagens(win);
  win.focus();
  win.print();
};

// ── Valor por extenso (padrão de recibo brasileiro) ────────────
const UNIDADES = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
const DEZ_A_DEZENOVE = ['dez', 'onze', 'doze', 'treze', 'catorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
const DEZENAS = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
const CENTENAS = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

function grupoPorExtenso(n) {
  if (n === 0) return '';
  if (n === 100) return 'cem';
  const c = Math.floor(n / 100), resto = n % 100;
  const partes = [];
  if (c) partes.push(CENTENAS[c]);
  if (resto) {
    if (resto < 10) partes.push(UNIDADES[resto]);
    else if (resto < 20) partes.push(DEZ_A_DEZENOVE[resto - 10]);
    else {
      const d = Math.floor(resto / 10), u = resto % 10;
      partes.push(DEZENAS[d] + (u ? ` e ${UNIDADES[u]}` : ''));
    }
  }
  return partes.join(' e ');
}

// Só até 999.999.999,99 — mais que suficiente para um recibo de venda/CR.
export function valorPorExtenso(valor) {
  const v = Math.round(Math.abs(parseFloat(valor) || 0) * 100);
  const inteiros = Math.floor(v / 100);
  const centavos = v % 100;

  let textoInteiro;
  if (inteiros === 0) {
    textoInteiro = 'zero reais';
  } else {
    const milhoes  = Math.floor(inteiros / 1000000);
    const milhares = Math.floor((inteiros % 1000000) / 1000);
    const centenas = inteiros % 1000;
    const grupos = [];
    if (milhoes)  grupos.push(`${grupoPorExtenso(milhoes)} ${milhoes === 1 ? 'milhão' : 'milhões'}`);
    if (milhares) grupos.push(`${milhares === 1 ? 'mil' : grupoPorExtenso(milhares) + ' mil'}`);
    if (centenas || grupos.length === 0) grupos.push(grupoPorExtenso(centenas) || 'zero');
    // "de reais" só quando milhão(ões) é o último grupo antes de "reais"
    // (ex.: "um milhão de reais"; mas "um milhão e cem reais" sem "de")
    const precisaDe = milhoes > 0 && milhares === 0 && centenas === 0;
    textoInteiro = grupos.join(' e ') + (precisaDe ? ' de' : '') + (inteiros === 1 ? ' real' : ' reais');
  }

  if (centavos === 0) return textoInteiro;
  const textoCentavos = `${grupoPorExtenso(centavos)} ${centavos === 1 ? 'centavo' : 'centavos'}`;
  return `${textoInteiro} e ${textoCentavos}`;
}

// ── DANFSe (Documento Auxiliar da NFS-e) — padrão nacional, A4 ──
// A partir do resultado de POST /fiscal/nfse: `resultado.sucesso` vem da
// transmissão real (fiscalController.emitirNfse — assina, transmite e volta
// autorizada/rejeitada, ver 0f6f3ad). Achado ao vivo na RBDIGITAL: até aqui
// esta função checava `resultado.preview`, um campo que só existia na fase
// anterior (fundação, resposta fake com 501) — nunca existiu na resposta
// real, então o watermark "NÃO TRANSMITIDA" aparecia SEMPRE, mesmo numa
// emissão de verdade autorizada pelo Sistema Nacional.
// `winPreAberta`: passe uma aba já aberta (window.open síncrono, antes de
// qualquer await) quando o resultado só é conhecido depois de uma chamada
// de rede — abrir a aba SÓ AQUI, depois do await, é tarde demais pro
// navegador ainda considerar "resposta direta a um clique" e acaba
// bloqueando o pop-up (achado ao vivo — ver emitirNfseItem no PDV/Pedidos).
export const gerarPreviewDanfSe = async (resultado, org = {}, winPreAberta = null) => {
  const d = resultado?.dados || {};
  const w = winPreAberta || window.open('', '_blank', 'width=820,height=900');
  if (!w) { alert('Permita pop-ups para imprimir.'); return; }
  w.document.write('<html><body style="font-family:sans-serif;padding:8px;">Gerando DANFSe…</body></html>');

  // Autorizada, mas SEM dados pra montar nem o rascunho local (ex.: "este
  // item já possui NFS-e autorizada" — o item já foi emitido antes, então
  // a resposta não vem com `dados`) — não faz sentido cair no template
  // cheio de campos zerados, que mais confunde do que ajuda. Mostra só o
  // que se sabe (número/chave, se vieram) e onde ver o documento oficial
  // caso o /danfse do Sistema Nacional esteja indisponível (achado ao vivo:
  // instabilidade documentada desse endpoint específico).
  if (resultado?.sucesso === true && !resultado?.dados) {
    w.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
      <title>NFS-e já emitida</title></head>
      <body style="font-family:Arial,sans-serif;padding:24px;max-width:480px;">
        <h2 style="color:#4c1d95;">NFS-e já emitida e autorizada</h2>
        ${resultado.numero_nfse ? `<p><strong>Número:</strong> ${resultado.numero_nfse}</p>` : ''}
        ${resultado.chave_acesso ? `<p style="word-break:break-all;"><strong>Chave de acesso:</strong> ${resultado.chave_acesso}</p>` : ''}
        <p style="margin-top:16px;color:#92400e;background:#fff3cd;border:1px solid #f59e0b;border-radius:6px;padding:10px;">
          ⚠ Não foi possível buscar o PDF oficial agora (o serviço de DANFSe do Sistema
          Nacional está instável). Tente de novo em instantes, ou consulte pela
          chave de acesso em
          <a href="https://www.nfse.gov.br/EmissorNacional/" target="_blank" rel="noreferrer">nfse.gov.br/EmissorNacional</a>.
        </p>
      </body></html>`);
    return;
  }

  const logoData = await fetchLogoBase64(org.logo_url || '');
  const naoTransmitida = resultado?.sucesso !== true;
  const cnpjFmt = doc => {
    const n = String(doc || '').replace(/\D/g, '');
    if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return doc || '';
  };
  const fmtDT = iso => iso ? new Date(iso).toLocaleString('pt-BR') : '—';

  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>DANFSe — ${d.prestNome || ''}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:Arial,sans-serif; font-size:12px; color:#111; padding:20px; }
  .watermark { background:#fff3cd; border:2px solid #f59e0b; color:#92400e; text-align:center;
    font-weight:bold; padding:8px; margin-bottom:12px; letter-spacing:0.5px; }
  .cabecalho { display:flex; align-items:center; gap:14px; border-bottom:2px solid #4c1d95; padding-bottom:10px; margin-bottom:10px; }
  .titulo { text-align:right; margin-left:auto; }
  .titulo h1 { font-size:15px; color:#4c1d95; }
  .titulo p { font-size:10px; color:#666; }
  .secao { border:1px solid #ddd; border-radius:6px; padding:10px 12px; margin-bottom:10px; }
  .secao h2 { font-size:11px; color:#4c1d95; text-transform:uppercase; margin-bottom:6px; }
  .linha { display:flex; justify-content:space-between; padding:2px 0; }
  .valores { background:#f5f3ff; }
  .valores .total { font-size:16px; font-weight:bold; color:#4c1d95; }
  .rodape { margin-top:16px; font-size:9px; color:#888; text-align:center; }
</style></head><body>
  ${naoTransmitida ? `<div class="watermark">⚠ ${resultado?.status
    ? `REJEITADA PELO SISTEMA NACIONAL (status ${resultado.status})${resultado.motivo ? ' — ' + resultado.motivo : ''}`
    : 'DOCUMENTO NÃO TRANSMITIDO'} — SEM VALOR FISCAL</div>` : ''}
  <div class="cabecalho">
    ${logoData ? `<img src="${logoData}" style="max-height:50px;max-width:110px;object-fit:contain;" />` : ''}
    <div>
      <div style="font-weight:bold;font-size:13px;">${d.prestNome || org.razao_social || ''}</div>
      <div style="font-size:10px;color:#555;">CNPJ: ${cnpjFmt(d.prestCnpj)}${d.prestIm ? ` · IM: ${d.prestIm}` : ''}</div>
      <div style="font-size:10px;color:#555;">${d.prestEndereco || ''}</div>
    </div>
    <div class="titulo">
      <h1>DANFSe</h1>
      <p>Documento Auxiliar da NFS-e — Padrão Nacional</p>
      <p>${naoTransmitida
        ? `nDPS ${d.numero || '—'} · Série ${d.serie || '—'}`
        : `Nº ${resultado.numero_nfse || '—'} · Série ${d.serie || '—'}`}</p>
    </div>
  </div>

  <div class="secao">
    <h2>Tomador do Serviço</h2>
    ${d.tomaNome ? `<div class="linha"><span>${d.tomaNome}</span><span>${cnpjFmt(d.tomaDoc)}</span></div>` : '<div style="color:#888;">Não identificado</div>'}
  </div>

  <div class="secao">
    <h2>Discriminação do Serviço</h2>
    <div class="linha"><span>${d.servDescricao || ''}</span></div>
    <div class="linha" style="font-size:10px;color:#666;margin-top:4px;">
      <span>Código de tributação nacional: ${resultado.c_trib_nac || '—'}</span>
      <span>Local da prestação (IBGE): ${d.cLocEmi || '—'}</span>
    </div>
  </div>

  <div class="secao valores">
    <div class="linha"><span>Valor do Serviço</span><span>R$ ${fmtBRL(d.vServ)}</span></div>
    ${d.pAliq != null ? `<div class="linha"><span>Alíquota ISSQN</span><span>${d.pAliq}%</span></div>` : ''}
    <div class="linha total"><span>Valor Líquido</span><span>R$ ${fmtBRL(d.vServ)}</span></div>
  </div>

  <div class="secao">
    <h2>Identificação da DPS</h2>
    <div class="linha"><span>Emissão</span><span>${fmtDT(d.dhEmi)}</span></div>
    <div class="linha"><span>Competência</span><span>${d.dCompet || '—'}</span></div>
    <div class="linha" style="font-size:9px;word-break:break-all;color:#888;"><span>${resultado.id_dps || ''}</span></div>
    ${!naoTransmitida && resultado.chave_acesso ? `<div class="linha" style="font-size:9px;word-break:break-all;"><span>Chave de acesso: ${resultado.chave_acesso}</span></div>` : ''}
  </div>

  <div class="rodape">
    ${naoTransmitida
      ? (resultado?.status ? 'DPS transmitida e rejeitada pelo Sistema Nacional NFS-e — corrija e emita de novo.' : 'Nenhum dado foi transmitido ao Sistema Nacional NFS-e.')
      : 'DANFSe referente à NFS-e autorizada no Sistema Nacional.'}
  </div>
</body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
  fecharComEsc(w);
  await aguardarImagens(w);
  w.focus();
  w.print();
};

// ── DANFSe oficial (documento gerado pelo próprio Sistema Nacional) ──
// Chamar SÓ depois de uma emissão com sucesso (resultado.documento_id +
// resultado.sucesso). Busca o PDF de verdade — com QR code e leiaute
// oficial, não o rascunho HTML de gerarPreviewDanfSe() acima. Quando o
// /danfse do Sistema Nacional está fora do ar (instabilidade documentada —
// 503 contínuo testado exaustivamente), o backend responde 200 com JSON
// {facsimile:true, dadosDanfse} em vez do PDF: cai pro facsímile local
// (imprimirDanfseFacsimile), que segue o mesmo leiaute oficial (NT nº
// 008/2026) montado com os dados REAIS da própria emissão — não mais o
// rascunho "tudo zerado" de gerarPreviewDanfSe.
// `winPreAberta`: ver aviso em gerarPreviewDanfSe — mesma razão.
export const abrirDanfseOficial = async (resultado, org = {}, winPreAberta = null) => {
  const win = winPreAberta || window.open('', '_blank');
  if (win) win.document.write('<html><body style="font-family:sans-serif;padding:20px">Buscando DANFSe no Sistema Nacional…</body></html>');
  try {
    const { data } = await api.get(`/fiscal/nfse/${resultado.documento_id}/danfse-pdf`, { responseType: 'blob' });
    if (!win) { alert('Permita pop-ups para abrir o DANFSe.'); return; }
    if (data.type && data.type.includes('application/pdf')) {
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      win.location.href = url;
      return;
    }
    // Não veio PDF — o backend respondeu com o JSON do facsímile (ver
    // fiscalController.nfseDanfsePdf). O blob content-type já denuncia isso
    // (application/json), mas o corpo em si só dá pra ler via texto.
    const texto = await data.text();
    const json = JSON.parse(texto);
    if (json?.facsimile && json?.dadosDanfse) {
      await imprimirDanfseFacsimile(json, org, win);
      return;
    }
    throw new Error(json?.erro || 'Resposta inesperada do servidor.');
  } catch (e) {
    try { win?.close(); } catch (_) {}
    await gerarPreviewDanfSe(resultado, org); // nem PDF oficial nem facsímile local — cai no rascunho mínimo
  }
};

// ── Facsímile local do DANFSe (Sistema Nacional NFS-e indisponível) ──
// Reproduz o leiaute oficial descrito na Nota Técnica nº 008/2026
// (SE/CGNFS-e) — "Especificações Técnicas do DANFSe": mesma ordem de
// blocos, mesmos rótulos de campo, QR Code apontando pro endereço oficial
// de consulta pública ("https://www.nfse.gov.br/ConsultaPublica/?tpc=1&
// chave=") e sombreamento cinza claro no cabeçalho/títulos de bloco/campo
// "Emitente da NFS-e"/"Valor Líquido da NFS-e + IBS/CBS", conforme o
// item 2.2.3 da norma. `resposta` é o JSON de GET /fiscal/nfse/:id/danfse-pdf
// quando ele NÃO conseguiu o PDF oficial: {dadosDanfse, numero, serie,
// chave_acesso, status, data_autorizacao} — dadosDanfse vem de
// nfseService.parseXmlParaDanfse(xml_retorno), o XML real devolvido pelo
// Sefin Nacional na hora da emissão (não é um rascunho com campo inventado:
// tudo que aparece aqui é o que a NFS-e de verdade tem gravado).
const OP_SIMP_NAC = { '1': 'Não Optante', '2': 'Optante', '3': 'Optante - Microempresa ou Empresa de Pequeno Porte (ME/EPP)' };
const REG_AP_TRIB_SN = {
  '1': 'Regime de apuração dos tributos federais e municipal pelo Simples Nacional',
  '2': 'Regime de apuração dos tributos federais pelo Simples Nacional e municipal pelo regime normal',
  '3': 'Regime de apuração dos tributos federais e municipal pelo regime normal',
};
const TP_EMIT_NFSE  = { '1': 'Prestador', '2': 'Tomador/Intermediário', '3': 'Terceiro (ordem de serviço)' };
const TP_RET_ISSQN   = { '1': 'Não Retido', '2': 'Retido pelo Tomador', '3': 'Retido pelo Intermediário' };
const TRIB_ISSQN_TXT = {
  '1': 'Operação Tributável', '2': 'Imunidade', '3': 'Exportação de Serviço', '4': 'Não Incidência',
};
const cStatDanfse = c => c === '100' || c === 100 ? 'NFS-e Gerada' : (c ? `Status ${c}` : '-');

// nn.nn.nn (cTribNac tem 6 dígitos) / n.nnnn.nn.nn (cNBS tem 9 dígitos) —
// mesma pontuação vista no DANFSe oficial baixado do Portal Contribuinte
// (nº 269, RBDIGITAL) usado de referência pra este facsímile.
const fmtCTribNac = c => c && c.length === 6 ? `${c.slice(0,2)}.${c.slice(2,4)}.${c.slice(4,6)}` : (c || '-');
const fmtCNBS = c => c && c.length === 9 ? `${c.slice(0,1)}.${c.slice(1,5)}.${c.slice(5,7)}.${c.slice(7,9)}` : (c || '-');

export const imprimirDanfseFacsimile = async (resposta, org = {}, winPreAberta = null) => {
  const d = resposta.dadosDanfse || {};
  const w = winPreAberta || window.open('', '_blank', 'width=900,height=1000');
  if (!w) { alert('Permita pop-ups para imprimir.'); return; }
  w.document.write('<html><body style="font-family:sans-serif;padding:8px;">Montando o DANFSe…</body></html>');

  const V = x => (x === null || x === undefined || x === '' ? '-' : x);
  const money = x => (x === null || x === undefined || x === '' ? '-' : `R$ ${fmtBRL(x)}`);
  const fmtDataHora = iso => iso ? new Date(iso).toLocaleString('pt-BR') : '-';
  const cnpjFmt = doc => {
    const n = String(doc || '').replace(/\D/g, '');
    if (n.length === 14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    if (n.length === 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return doc ? String(doc) : '-';
  };
  const municipioUf = (municipio, uf) => municipio ? `${municipio}${uf ? ' / ' + uf : ''}` : '-';

  const chave = resposta.chave_acesso || '';
  const qrUrl = chave ? `https://www.nfse.gov.br/ConsultaPublica/?tpc=1&chave=${chave}` : '';
  let qrDataUrl = '';
  if (qrUrl) {
    try { qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 1, errorCorrectionLevel: 'M' }); } catch (_) {}
  }
  const semValidadeJuridica = String(d.tpAmb) === '2';
  const cancelada = resposta.status === 'cancelada';

  // Um "campo" = rótulo em negrito (6pt) em cima do conteúdo (7pt) — regra
  // de fonte do item 2.4.1/2.4.2 da NT 008/2026. `w` é a largura em cm
  // (mesma unidade usada na norma, que a impressão do navegador respeita
  // de verdade em @media print).
  const campo = (label, valor, larguraCm, sombreado) => `
    <div style="width:${larguraCm}cm;padding:2px 4px;${sombreado ? 'background:#f2f2f2;' : ''}">
      <div style="font-weight:bold;font-size:6pt;">${label}</div>
      <div style="font-size:7pt;word-break:break-word;">${valor}</div>
    </div>`;
  const linha = (...campos) => `<div style="display:flex;border-top:0.5pt solid #999;">${campos.join('')}</div>`;
  const tituloBloco = texto => `<div style="background:#f2f2f2;font-weight:bold;font-size:7pt;text-transform:uppercase;padding:3px 4px;border-top:1pt solid #000;">${texto}</div>`;
  const barraTexto = texto => `<div style="border-top:0.5pt solid #999;padding:4px;font-size:7pt;color:#555;text-align:center;">${texto}</div>`;

  const blocoPessoa = (titulo, { cnpj, im, fone, nome, municipio, uf, cMun, cep, endereco, email }, semDados, msgSemDados) => {
    if (semDados) return `${tituloBloco(titulo)}${barraTexto(msgSemDados)}`;
    return `${tituloBloco(titulo)}
      ${linha(
        campo('CNPJ / CPF / NIF', cnpjFmt(cnpj), 5.09),
        campo('Indicador Municipal (Inscrição)', V(im), 5.09),
        campo('Telefone', V(fone), 5.09),
      )}
      ${linha(
        campo('Nome / Nome Empresarial', V(nome), 10.19),
        campo('Município / Sigla UF', municipioUf(municipio, uf), 5.09),
      )}
      ${linha(
        campo('Endereço', V(endereco), 10.19),
        campo('Código IBGE / CEP', `${V(cMun)} / ${V(cep)}`, 5.09),
        campo('E-mail', V(email), 5.09),
      )}`;
  };

  const enderecoPrest = [d.prestLogradouro, d.prestNumero, d.prestBairro].filter(Boolean).join(', ');
  const enderecoToma = [d.tomaLogradouro, d.tomaNumero, d.tomaComplemento, d.tomaBairro].filter(Boolean).join(', ');

  const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8"><title>DANFSe nº ${V(resposta.numero)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:Arial,'Microsoft Sans Serif',sans-serif; color:#111; padding:0.3cm; }
  .pagina { width:20.4cm; margin:0 auto; border:1pt solid #000; position:relative; }
  .aviso-homolog { text-align:center; color:#c00; font-weight:bold; font-size:9pt; padding:2px 0; }
  .marca-dagua { position:absolute; top:45%; left:8%; font-size:70pt; color:rgba(120,120,120,0.28);
    font-weight:bold; transform:rotate(-28deg); z-index:5; pointer-events:none; }
  @media print { body { padding:0; } .pagina { border:1pt solid #000; } }
</style></head><body>
  <div class="pagina">
    ${cancelada ? '<div class="marca-dagua">CANCELADA</div>' : ''}
    <div style="display:flex;align-items:center;border-bottom:1pt solid #000;padding:4px 6px;">
      <div style="width:4cm;font-weight:bold;color:#0d9488;font-size:13pt;">NFS<span style="color:#111;">e</span>
        <div style="font-size:6pt;font-weight:normal;color:#555;">Nota Fiscal de Serviço eletrônica</div>
      </div>
      <div style="flex:1;text-align:center;">
        <div style="font-weight:bold;font-size:9pt;">DANFSe v2.0</div>
        <div style="font-size:9pt;">Documento Auxiliar da NFS-e</div>
        ${semValidadeJuridica ? '<div class="aviso-homolog">NFS-e SEM VALIDADE JURÍDICA</div>' : ''}
      </div>
      <div style="width:5.09cm;font-size:8pt;text-align:right;">
        <div>Município: ${V(d.prestCidade || d.xLocEmi)} - ${V(d.prestUf)}</div>
        <div style="font-size:6pt;">Ambiente Gerador: ${V(d.ambienteGerador)}</div>
        <div style="font-size:6pt;">Tipo de Ambiente: ${V(d.tpAmb)}</div>
      </div>
    </div>

    <div style="display:flex;">
      <div style="flex:1;">
        ${linha(campo('Chave de Acesso da NFS-e', chave || '-', 15.30))}
        ${linha(
          campo('Número da NFS-e', V(resposta.numero), 5.09),
          campo('Competência da NFS-e', V(d.dCompet), 5.09),
          campo('Data e Hora da Emissão da NFS-e', fmtDataHora(d.dhProc), 5.09),
        )}
        ${linha(
          campo('Número da DPS', V(d.numeroDps), 5.09),
          campo('Série da DPS', V(resposta.serie || d.serieDps), 5.09),
          campo('Data e Hora da Emissão da DPS', fmtDataHora(d.dhEmiDps), 5.09),
        )}
        ${linha(
          campo('Emitente da NFS-e', V(TP_EMIT_NFSE['1']), 5.09, true),
          campo('Situação da NFS-e', cStatDanfse(resposta.status === 'autorizada' ? '100' : null), 5.09),
          campo('Finalidade', '-', 5.09),
        )}
      </div>
      <div style="width:5.1cm;padding:4px;text-align:center;border-left:0.5pt solid #999;">
        ${qrDataUrl ? `<img src="${qrDataUrl}" style="width:2.2cm;height:2.2cm;" />` : ''}
        <div style="font-size:6pt;color:#555;margin-top:2px;">A autenticidade desta NFS-e pode ser verificada
          pela leitura deste código QR ou pela consulta da chave de
          acesso no portal nacional da NFS-e</div>
      </div>
    </div>

    ${blocoPessoa('Prestador / Fornecedor', {
      cnpj: d.prestCnpj, im: d.prestIm, fone: d.prestFone, nome: d.prestNome,
      municipio: d.prestCidade || d.xLocEmi, uf: d.prestUf, cMun: '-', cep: d.prestCep, endereco: enderecoPrest, email: '-',
    }, false, '')}
    ${linha(
      campo('Simples Nacional na Data de Competência', V(OP_SIMP_NAC[d.opSimpNac]), 5.09),
      campo('Regime de Apuração Tributária pelo SN', V(REG_AP_TRIB_SN[d.regApTribSN]), 10.19),
    )}

    ${blocoPessoa('Tomador / Adquirente', {
      cnpj: d.tomaCnpj, im: '-', fone: d.tomaFone, nome: d.tomaNome,
      municipio: d.tomaCidade, uf: d.tomaUf, cMun: '-', cep: d.tomaCep, endereco: enderecoToma, email: d.tomaEmail,
    }, !d.tomaCnpj, 'TOMADOR/ADQUIRENTE DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e')}

    ${tituloBloco('Destinatário da Operação')}
    ${barraTexto('DESTINATÁRIO DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e')}

    ${tituloBloco('Intermediário da Operação')}
    ${barraTexto('INTERMEDIÁRIO DA OPERAÇÃO NÃO IDENTIFICADO NA NFS-e')}

    ${tituloBloco('Serviço Prestado')}
    ${linha(
      campo('Código de Tributação Nacional/Municipal', `${fmtCTribNac(d.cTribNac)} / -`, 5.09),
      campo('Código da NBS', fmtCNBS(d.cNBS), 5.09),
      campo('Local da Prestação / Sigla UF / País', `${V(d.xLocPrestacao)} / ${V(d.prestUf)} / -`, 10.19),
    )}
    ${linha(campo('', V(d.xTribNac), 20.40))}
    ${linha(campo('Descrição do Serviço', V(d.xDescServ), 20.40))}

    ${tituloBloco('Tributação Municipal (ISSQN)')}
    ${linha(
      campo('Tipo de Tributação do ISSQN', V(TRIB_ISSQN_TXT[d.tribISSQN]), 5.09),
      campo('Município / Sigla UF / País de Incidência do ISSQN', `${municipioUf(d.xLocIncid || d.prestCidade, d.prestUf)} / -`, 10.19),
    )}
    ${linha(
      campo('BC ISSQN', '-', 5.09),
      campo('Alíquota Aplicada', '-', 5.09),
      campo('Retenção do ISSQN', V(TP_RET_ISSQN[d.tpRetISSQN]), 5.09),
      campo('ISSQN Apurado', '-', 5.09),
    )}

    ${tituloBloco('Tributação Federal (Exceto CBS)')}
    ${linha(
      campo('IRRF', '-', 5.09),
      campo('Contribuição Previdenciária - Retida', '-', 5.09),
      campo('Contribuições Sociais - Retidas', '-', 5.09),
    )}
    ${linha(
      campo('PIS - Débito Apuração Própria', '-', 5.09),
      campo('COFINS - Débito Apuração Própria', '-', 5.09),
      campo('Descrição Contrib. Sociais - Retidas', '-', 10.19),
    )}

    <!-- Tributação IBS/CBS: reforma tributária ainda não em vigor pras
         operações que emitimos hoje — nossa DPS não preenche nenhum campo
         desse grupo, igual ao DANFSe oficial de referência (nº 269, RBDIGITAL),
         que veio com "-" em tudo aqui (só "Exclusões e Reduções" sai
         R$ 0,00, por ser somatório de campos todos vazios). -->
    ${tituloBloco('Tributação IBS/CBS')}
    ${linha(campo('CST / cClassTrib', '- / -', 5.09), campo('Indicador de Operação / Código IBGE Incidência / Município Incidência / Sigla UF', '- / - / - / -', 10.19))}
    ${linha(campo('Exclusões e Reduções da Base de Cálculo', 'R$ 0,00', 5.09), campo('Base de Cálculo Após Exclusões e Reduções', '-', 5.09))}
    ${linha(campo('Red. Alíquota IBS / Red. Alíquota CBS', '- / - / -', 5.09), campo('Alíquota - IBS UF / IBS Mun', '- / -', 5.09))}
    ${linha(campo('Alíq. Efetiva Municipal - IBS', '-', 5.09), campo('Valor Apurado Municipal - IBS', '-', 5.09), campo('Alíq. Efetiva Estadual - IBS', '-', 5.09), campo('Valor Apurado Estadual - IBS', '-', 5.09))}
    ${linha(campo('Valor Total Apurado - IBS', '-', 5.09), campo('Alíquota - CBS', '-', 5.09), campo('Alíquota Efetiva - CBS', '-', 5.09), campo('Valor Total Apurado - CBS', '-', 5.09))}

    ${tituloBloco('Valor Total da NFS-e')}
    ${linha(
      campo('Valor da Operação / Serviço', money(d.vServ), 6.8),
      campo('Desconto Incondicionado', '-', 6.8),
      campo('Desconto Condicionado', '-', 6.8),
    )}
    ${linha(
      campo('Total das Retenções (ISSQN / Federais)', '-', 5.09),
      campo('Valor Líquido da NFS-e', money(d.vLiq), 5.09),
      campo('Total do IBS/CBS', 'R$ 0,00', 5.09),
      campo('Valor Líquido da NFS-e + IBS/CBS', 'R$ 0,00', 5.09, true),
    )}

    ${tituloBloco('Informações Complementares')}
    ${barraTexto(`Totais aproximados dos Tributos cfe. Lei nº 12.741/2012: Federais: -; Estaduais: -; Municipais: ${d.pTotTribSN ? d.pTotTribSN + '%' : '-'};`)}

    <div style="border-top:1pt dashed #999;margin-top:10px;padding-top:6px;display:flex;">
      ${campo('Data Cientificação', '', 5.09)}
      ${campo('Identificação e Assinatura', '', 5.09)}
      ${campo('Nº NFS-e / Chave NFS-e', `${V(resposta.numero)} / ${chave || '-'}`, 10.19)}
    </div>

    <div style="text-align:center;font-size:7pt;color:#888;margin-top:8px;padding-bottom:4px;">
      Consulte a chave de acesso em <b>nfse.gov.br/ConsultaPublica</b> pra ver/baixar o DANFSe direto do portal do governo.
    </div>
  </div>
</body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
  fecharComEsc(w);
  await aguardarImagens(w);
  w.focus();
  w.print();
};

// ── Recibo de pagamento (Contas a Receber) — A4 ou térmica ─────
// `cr` vem de GET /financeiro/contas-receber/:id/recibo (só título recebido).
export const gerarReciboPagamento = async (cr, org = {}, largura = 'a4') => {
  const { nome, cnpj, endereco, cidadeUF, fone } = dadosOrg(org);
  // O recibo mostra o LÍQUIDO efetivamente recebido — pode diferir do valor
  // nominal do título quando houve desconto/juros negociados no recebimento.
  const valorOriginal = parseFloat(cr.valor || 0);
  const vDesconto = parseFloat(cr.desconto_recebimento || 0);
  const vJuros    = parseFloat(cr.juros_recebimento || 0);
  const temAjusteRecibo = vDesconto > 0.004 || vJuros > 0.004;
  const valor = cr.valor_liquido_recebido != null ? parseFloat(cr.valor_liquido_recebido) : valorOriginal;
  const dataReceb = cr.data_recebimento
    ? new Date(cr.data_recebimento + 'T12:00:00').toLocaleDateString('pt-BR')
    : '—';
  const enderecoCliente = [cr.logradouro, cr.numero, cr.complemento, cr.bairro]
    .filter(Boolean).join(', ');
  const cidadeUFCliente = [cr.cidade, cr.estado].filter(Boolean).join('/');
  const docCliente = cr.cpf_cnpj
    ? `${String(cr.cpf_cnpj).replace(/[^A-Za-z0-9]/g,'').length > 11 ? 'CNPJ' : 'CPF'}: ${cr.cpf_cnpj}`
    : '';
  const parcelaTxt = cr.total_parcelas > 1 ? ` — Parcela ${cr.parcela}/${cr.total_parcelas}` : '';
  const extenso = valorPorExtenso(valor);
  const extensoCap = extenso.charAt(0).toUpperCase() + extenso.slice(1);

  if (largura !== 'a4') {
    // Térmica — mesma linguagem visual do cupom
    const larguraCSS = larguraEfetivaCSS(largura);
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Recibo #${cr.id}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:10px; font-weight:600; width:${larguraCSS}; padding:4px; }
  .c { text-align:center; } .b { font-weight:bold; }
  .hr { border:none; border-top:1px dashed #000; margin:4px 0; }
  .row { display:flex; justify-content:space-between; }
  @media print { @page { margin:0; size:${larguraCSS} auto; } }
</style></head><body>
  <p class="c b" style="font-size:12px;">${nome}</p>
  ${cnpj ? `<p class="c">CNPJ: ${cnpj}</p>` : ''}
  <hr class="hr">
  <p class="c b">RECIBO DE PAGAMENTO</p>
  <p class="c">Nº ${cr.id}${parcelaTxt}</p>
  <hr class="hr">
  <p>Recebi de: <b>${cr.cliente_nome || 'Consumidor Final'}</b></p>
  ${docCliente ? `<p>${docCliente}</p>` : ''}
  <p>Referente a: ${cr.descricao || '—'}</p>
  <hr class="hr">
  ${temAjusteRecibo ? `<p class="row" style="font-size:9px;"><span>Valor original</span><span>R$ ${fmtBRL(valorOriginal)}</span></p>
  ${vDesconto > 0.004 ? `<p class="row" style="font-size:9px;"><span>Desconto</span><span>- R$ ${fmtBRL(vDesconto)}</span></p>` : ''}
  ${vJuros > 0.004 ? `<p class="row" style="font-size:9px;"><span>Juros</span><span>+ R$ ${fmtBRL(vJuros)}</span></p>` : ''}` : ''}
  <p class="row"><span>Valor${temAjusteRecibo ? ' líquido' : ''}</span><span class="b">R$ ${fmtBRL(valor)}</span></p>
  <p style="margin-top:4px;">${extensoCap}.</p>
  <hr class="hr">
  <p class="row"><span>Data recebimento</span><span>${dataReceb}</span></p>
  <p class="row"><span>Forma</span><span>${cr.forma_recebimento || '—'}</span></p>
  <hr class="hr">
  <p class="c" style="margin-top:20px;">_______________________________</p>
  <p class="c">${nome}</p>
</body></html>`;
    abrirEImprimir(html, largura);
    return;
  }

  // A4 — recibo formal completo
  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Recibo #${cr.id}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:Arial,sans-serif; font-size:12px; color:#000; padding:24px 32px; }
  .cabecalho { text-align:center; border-bottom:2px solid #000; padding-bottom:10px; margin-bottom:18px; }
  .cabecalho .razao { font-size:16px; font-weight:bold; }
  .titulo { text-align:center; font-size:18px; font-weight:bold; letter-spacing:1px; margin-bottom:4px; }
  .nr { text-align:center; font-size:12px; color:#555; margin-bottom:20px; }
  .valor-destaque { text-align:center; font-size:26px; font-weight:bold; border:2px solid #000;
    padding:10px; margin:16px 0; }
  .corpo { line-height:2; text-align:justify; margin:20px 0; font-size:13px; }
  .info-tabela { width:100%; border-collapse:collapse; margin:20px 0; }
  .info-tabela td { padding:5px 8px; border:1px solid #ccc; font-size:11px; }
  .info-tabela td.label { background:#f5f5f5; font-weight:bold; width:160px; }
  .assinatura { margin-top:60px; text-align:center; }
  .assinatura .linha { border-top:1px solid #000; width:320px; margin:0 auto 4px; }
  .rodape { text-align:center; font-size:9px; color:#888; margin-top:30px; }
  @media print { @page { size:A4 portrait; margin:18mm 20mm; } body { padding:0; } }
</style></head><body>

<div class="cabecalho">
  <div class="razao">${nome}</div>
  ${cnpj ? `<div>CNPJ: ${cnpj}</div>` : ''}
  ${endereco ? `<div>${endereco}${cidadeUF ? ' — ' + cidadeUF : ''}</div>` : ''}
  ${fone ? `<div>${fone}</div>` : ''}
</div>

<div class="titulo">RECIBO DE PAGAMENTO</div>
<div class="nr">Nº ${cr.id}${parcelaTxt} — Emitido em ${new Date().toLocaleDateString('pt-BR')}</div>

<div class="valor-destaque">R$ ${fmtBRL(valor)}</div>

<div class="corpo">
  Recebi de <b>${cr.cliente_nome || 'Consumidor Final'}</b>${docCliente ? `, ${docCliente}` : ''}${enderecoCliente ? `, residente/sediado(a) em ${enderecoCliente}${cidadeUFCliente ? ' — ' + cidadeUFCliente : ''}` : ''},
  a importância de <b>R$ ${fmtBRL(valor)}</b> (${extenso}), referente a
  <b>${cr.descricao || 'pagamento'}</b>${cr.numero_documento ? `, documento nº ${cr.numero_documento}` : ''}.
  Para maior clareza, firmo o presente recibo, dando plena e geral quitação do valor acima referido.
</div>

<table class="info-tabela">
  ${temAjusteRecibo ? `<tr><td class="label">Valor original do título</td><td>R$ ${fmtBRL(valorOriginal)}</td></tr>` : ''}
  ${vDesconto > 0.004 ? `<tr><td class="label">Desconto concedido</td><td>- R$ ${fmtBRL(vDesconto)}</td></tr>` : ''}
  ${vJuros > 0.004 ? `<tr><td class="label">Juros recebidos</td><td>+ R$ ${fmtBRL(vJuros)}</td></tr>` : ''}
  <tr><td class="label">Data do recebimento</td><td>${dataReceb}</td></tr>
  <tr><td class="label">Forma de recebimento</td><td>${cr.forma_recebimento || '—'}</td></tr>
  ${cr.conta_nome ? `<tr><td class="label">Conta financeira</td><td>${cr.conta_nome}</td></tr>` : ''}
  ${cr.plano_descricao ? `<tr><td class="label">Categoria</td><td>${cr.plano_codigo ? cr.plano_codigo + ' — ' : ''}${cr.plano_descricao}</td></tr>` : ''}
  ${cr.observacoes ? `<tr><td class="label">Observações</td><td>${cr.observacoes}</td></tr>` : ''}
</table>

<div class="assinatura">
  <div class="linha"></div>
  ${nome}${cnpj ? ` — CNPJ ${cnpj}` : ''}
</div>

<div class="rodape">Recibo gerado eletronicamente — sem valor fiscal</div>

</body></html>`;

  abrirEImprimir(html, 'a4');
};

// ── Fechamento de caixa (térmica) ─────────────────────────────
// Recebe o `resumo` de GET /financeiro/turnos/:id/resumo ({turno, formas,
// movs, totais}) — o mesmo formato usado pela tela Fechamento de Caixa
// (Financeiro → Caixa/Fechamento) e pelo fechamento automático do PDV.
// Saldo Final usa a MESMA fórmula da tela (Saldo Inicial + vendas em
// dinheiro líquidas + efeito das movimentações do turno no caixa) — não
// duplicar esse cálculo em outro lugar, senão os dois relatórios divergem.
export const imprimirFechamentoCaixaTermico = (resumo, largura = '80mm') => {
  if (!resumo) return;
  const { turno, formas, movs, totais } = resumo;
  const larguraCSS = larguraEfetivaCSS(largura);
  const fmtDT = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  // Suprimento/sangria AUTOMÁTICO gerado pelo parâmetro "Realizar sangria/
  // suprimentos pelo PDV" (turno_saldo_id) fica de fora de "Total Suprimento"
  // /"Total Sangria" e do ajuste — o valor dele já está em Saldo Inicial
  // (abertura) ou é o próprio valor de fechamento sendo impresso, contá-lo
  // de novo aqui duplicaria o valor no Saldo Final.
  const somaMovs = tipos => (movs || [])
    .filter(m => tipos.includes(m.tipo) && !m.turno_saldo_id)
    .reduce((acc, m) => acc + parseFloat(m.valor || 0), 0);
  const totalSuprimento = somaMovs(['suprimento', 'entrada']);
  const totalSangria    = somaMovs(['sangria', 'saida', 'devolucao']);

  // Mesma lógica de ajusteMovsTurno (FechamentoCaixa.jsx): vendas/recebimento
  // já contam via "dinheiro" abaixo; o resto soma/subtrai pelo tipo (ou pela
  // conta do turno, quando movimentado por transferência com conta_id explícita).
  const dinheiro = parseFloat(
    (formas || []).find(f => f.forma_pagamento?.toLowerCase() === 'dinheiro')?.total || 0
  );
  const movsAjuste = (movs || []).reduce((acc, m) => {
    if (m.tipo === 'venda' || m.tipo === 'recebimento' || m.turno_saldo_id) return acc;
    const v = parseFloat(m.valor || 0);
    if (turno.conta_id) {
      if (m.conta_destino_id === turno.conta_id) return acc + v;
      if (m.conta_origem_id  === turno.conta_id) return acc - v;
      return acc;
    }
    if (['suprimento', 'entrada'].includes(m.tipo)) return acc + v;
    if (['sangria', 'saida', 'devolucao'].includes(m.tipo)) return acc - v;
    return acc;
  }, 0);
  const saldoFinal = parseFloat(turno.saldo_abertura || 0) + dinheiro + movsAjuste;

  const linhasFormas = (formas || []).map(f => {
    const label = `${f.forma_pagamento}${f.bandeira ? ` (${f.bandeira})` : ''}`;
    return `<p class="row"><span>${label}</span><span>R$ ${fmtBRL(f.total)}</span></p>`;
  }).join('');

  // Turno ainda aberto = conferência PARCIAL (impressa a qualquer momento do
  // dia, pra acompanhar o caixa) — mesmo relatório, mesmas legendas do
  // fechamento oficial, só com o título/obs deixando claro que não fechou
  // de verdade ainda (pra não confundir os dois papéis).
  const aberto = !turno.fechamento_em;

  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>${aberto ? 'Conferência Parcial' : 'Fechamento'} — Turno #${turno.id}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:10px; font-weight:600; width:${larguraCSS}; padding:4px; }
  .c { text-align:center; } .b { font-weight:bold; }
  .hr { border:none; border-top:1px dashed #000; margin:4px 0; }
  .row { display:flex; justify-content:space-between; }
  .grand { font-size:12px; }
  @media print { @page { margin:0; size:${larguraCSS} auto; } }
</style></head><body>
  <p class="c b" style="font-size:12px;">${aberto ? 'CONFERÊNCIA DE CAIXA' : 'FECHAMENTO DE CAIXA'}</p>
  ${aberto ? '<p class="c" style="font-size:9px;">(parcial — turno em aberto, não é o fechamento oficial)</p>' : ''}
  <p class="c">Turno #${turno.id}</p>
  <hr class="hr">
  <p>Conta: ${turno.conta_nome || '—'}</p>
  <p>Operador: ${turno.usuario_nome}</p>
  ${turno.fechamento_em && turno.fechado_por_nome && turno.fechado_por_nome !== turno.usuario_nome
    ? `<p>Fechado por: ${turno.fechado_por_nome}</p>` : ''}
  <p>Abertura: ${fmtDT(turno.abertura_em)}</p>
  <p>${turno.fechamento_em ? `Fechamento: ${fmtDT(turno.fechamento_em)}` : 'Status: turno em aberto'}</p>
  <hr class="hr">
  <p class="b">FORMAS DE PAGAMENTO</p>
  ${linhasFormas || '<p style="color:#555;">Nenhum pagamento no turno.</p>'}
  <hr class="hr">
  <p class="row"><span>Saldo Inicial</span><span>R$ ${fmtBRL(turno.saldo_abertura)}</span></p>
  <p class="row"><span>Total Suprimento</span><span>R$ ${fmtBRL(totalSuprimento)}</span></p>
  <p class="row"><span>Total Sangria</span><span>R$ ${fmtBRL(totalSangria)}</span></p>
  <p class="row"><span>Troco</span><span>R$ ${fmtBRL(totais?.total_troco)}</span></p>
  <p class="row"><span>Total de Vendas</span><span>R$ ${fmtBRL(totais?.total_vendas)}</span></p>
  <hr class="hr">
  <p class="row b grand"><span>SALDO FINAL</span><span>R$ ${fmtBRL(saldoFinal)}</span></p>
  <hr class="hr">
  <p class="c" style="font-size:9px;">Emitido em ${new Date().toLocaleString('pt-BR')}</p>
</body></html>`;

  abrirEImprimir(html, largura);
};

// ── Comprovante avulso de Sangria/Suprimento de caixa ──────────
// Recibo térmico de UM lançamento (não o fechamento inteiro) — pra o
// operador imprimir/assinar na hora da retirada, ou reimprimir depois a
// partir do extrato (TransferenciaContas) ou do resumo de turno
// (FechamentoCaixa). `m` é a linha de movimentacoes_financeiras (com os
// nomes de conta/usuário já resolvidos pelo backend, quando disponíveis).
const ROTULO_MOV = {
  sangria: 'SANGRIA DE CAIXA',
  suprimento: 'SUPRIMENTO DE CAIXA',
};

export const imprimirComprovanteMovimentacao = (m, largura = '80mm') => {
  if (!m) return;
  const larguraCSS = larguraEfetivaCSS(largura);
  const fmtDT = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
  const rotulo = ROTULO_MOV[m.tipo] || (m.tipo || '').toUpperCase();

  const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>Comprovante — ${rotulo}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:10px; font-weight:600; width:${larguraCSS}; padding:4px; }
  .c { text-align:center; } .b { font-weight:bold; }
  .hr { border:none; border-top:1px dashed #000; margin:4px 0; }
  .row { display:flex; justify-content:space-between; }
  .grand { font-size:13px; }
  @media print { @page { margin:0; size:${larguraCSS} auto; } }
</style></head><body>
  <p class="c b" style="font-size:12px;">${rotulo}</p>
  <p class="c" style="font-size:9px;">Comprovante avulso — lançamento #${m.id}</p>
  <hr class="hr">
  <p>Data/Hora: ${fmtDT(m.criado_em)}</p>
  ${m.conta_origem_nome  ? `<p>Conta origem: ${m.conta_origem_nome}</p>`   : ''}
  ${m.conta_destino_nome ? `<p>Conta destino: ${m.conta_destino_nome}</p>` : ''}
  <p>Descrição: ${m.descricao || '—'}</p>
  ${m.usuario_nome ? `<p>Lançado por: ${m.usuario_nome}</p>` : ''}
  <hr class="hr">
  <p class="row b grand"><span>VALOR</span><span>R$ ${fmtBRL(m.valor)}</span></p>
  <hr class="hr">
  <p style="margin-top:26px;">Assinatura: ___________________________</p>
  <p class="c" style="font-size:9px;margin-top:10px;">Emitido em ${new Date().toLocaleString('pt-BR')}</p>
</body></html>`;

  abrirEImprimir(html, largura);
};

// ── DANFE NF-e em PDF (gerado pelo backend) ────────────────────
export const abrirDanfeNfePdf = async (endpoint, method = 'get', body = null) => {
  // Abre a aba ANTES do fetch (assíncrono) pra não perder o contexto de
  // gesto do usuário e o navegador bloquear o pop-up — mesmo truque de
  // sempre. Só é descartada (win.close()) se a impressão direta der certo;
  // aí nunca chega a aparecer nada pro operador.
  const win = window.open('', '_blank');
  if (win) win.document.write('<html><body style="font-family:sans-serif;padding:20px">Gerando DANFE NF-e…</body></html>');
  try {
    const cfg = { responseType: 'blob' };
    const { data } = method === 'post'
      ? await api.post(endpoint, body, cfg)
      : await api.get(endpoint, cfg);

    if (impressaoDiretaAtiva()) {
      const base64 = await blobParaBase64(data);
      if (await tentarImprimirDiretoPdf(base64, win)) return;
    }

    if (!win) { alert('Permita pop-ups para abrir o DANFE.'); return; }
    const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    win.location.href = url;
  } catch (e) {
    try { win?.close(); } catch (_) {}
    // responseType:'blob' (necessário pro PDF de sucesso) faz o axios
    // devolver o corpo de um erro 4xx/5xx como Blob binário também — sem
    // isso, e.response.data.erro é sempre undefined e só sobra a mensagem
    // genérica do axios ("Request failed with status code 422"), escondendo
    // o motivo real (achado real: preview de DANFE avulso, 2026-09-16).
    // Precisa reler o Blob como texto/JSON pra recuperar a mensagem de
    // verdade que o backend mandou.
    let msg = e.message;
    const respData = e.response?.data;
    if (respData instanceof Blob) {
      try {
        const json = JSON.parse(await respData.text());
        msg = json.erro || msg;
        if (Array.isArray(json.detalhes) && json.detalhes.length) {
          msg += '\n' + json.detalhes.map(d => `${d.produto}: falta ${(d.campos_faltando || []).join(', ')}`).join('\n');
        }
      } catch (_) { /* corpo não era JSON (ex.: erro de proxy/HTML) — mantém e.message */ }
    } else if (respData?.erro) {
      msg = respData.erro;
    }
    alert('Erro ao gerar DANFE NF-e: ' + msg);
  }
};

// ── Abrir um boleto (PDF genérico ou página do Cora) ────────────
// Boleto origem='cora' tem página própria hospedada pelo Cora — QR code
// Pix, logo/apelido configurados na conta Cora deles e a descrição do
// título, nenhum desses 3 existe no PDF genérico (gerarBoletoPdf, layout
// FEBRABAN puro, usado só pelo CNAB400). Recebe o objeto do boleto (com
// id/origem/boleto_url — do retorno de POST /vendas ou GET /boletos).
export const abrirBoleto = boleto => {
  if (boleto?.origem === 'cora' && boleto?.boleto_url) {
    window.open(boleto.boleto_url, '_blank');
    return;
  }
  abrirDanfeNfePdf(`/financeiro/boletos/${boleto.id}/pdf`);
};
